import {useState} from 'react';
import {useDispatch} from 'react-redux';
import {toast} from 'react-toastify';
import {
  uploadFile,
  downloadFile,
  deleteFile,
  fetchGenericFiles,
  fetchResourceFiles,
  requestIndividualForecastExport,
    requestMarketReportExport,
  requestRawMeasurementsExport,
  fetchExports
} from '../../../../../slices/dataSlice';

/**
 * Custom hook to manage file actions (upload, download, delete, export)
 */
export const useFileActions = (selectedResource, globalSelectedResource) => {
  const dispatch = useDispatch();

  const [fileName, setFileName] = useState('');
  const [pickedFile, setPickedFile] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const resetForm = () => {
    setFileName('');
    setPickedFile(null);
    setEditingFile(null);
  };

  const handleFilePick = (e) => {
    const file = e.target.files?.[0] || null;
    setPickedFile(file);
    if (file && !fileName) setFileName(file.name);
  };

  const handleUpload = async (e, scope) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!fileName && !pickedFile) {
      toast.error('Please provide a file name or choose a file.');
      return;
    }
    if (scope === 'resource' && !selectedResource) {
      toast.error('Please select a resource.');
      return;
    }

    try {
      await dispatch(uploadFile({
        file: pickedFile,
        description: fileName,
        resourceId: scope === 'resource' ? selectedResource : null,
        editingFile
      })).unwrap();

      toast.success(editingFile ? 'File updated' : 'File uploaded');

      if (selectedResource) {
        dispatch(fetchResourceFiles({resourceId: selectedResource}));
      }
      dispatch(fetchGenericFiles());
      resetForm();
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    }
  };

  const handleDownload = (file) => {
    const raw = file.raw || file;
    const fileUrl = raw.file || raw.file_url || raw.url || raw.download_url;
    const filename = file.name || raw.filename || raw.name;

    if (fileUrl) {
      dispatch(downloadFile({fileUrl, filename}));
      return;
    }

    // Fallback demo
    const content = `Demo file: ${file.name}`;
    const blob = new Blob([content], {type: 'text/plain'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const confirmDelete = (file) => {
    setDeleteTarget(file);
    setDeleteOpen(true);
  };

  const performDelete = () => {
    if (!deleteTarget) return;

    const raw = deleteTarget.raw || deleteTarget;
    if (raw && raw.id) {
      dispatch(deleteFile({fileId: raw.id})).then(action => {
        if (!action.error) {
          toast.success('File deleted');
          if (selectedResource) {
            dispatch(fetchResourceFiles({resourceId: selectedResource}));
          }
          dispatch(fetchGenericFiles());
        } else {
          toast.error('Failed');
        }
      });
    } else {
      toast.success('File deleted (demo)');
    }

    setDeleteOpen(false);
    setDeleteTarget(null);
  };

  const handleExportDownload = (item, resourceName) => {
    const status = (item.status || item.state || '').toString().toLowerCase();
    const id = item.id || item.server_id || item.job_id;

    if ((status === 'completed' || status === 'ready') && id) {
      dispatch(downloadFile({
        fileUrl: `/data/exports/${id}/download`,
        filename: item.filename || item.file_name || `${resourceName}_export`
      }));
      return;
    }

    const url = item.file_url || item.download_url || item.fileUrl || item.raw?.file_url || null;
    if (url) {
      dispatch(downloadFile({
        fileUrl: url,
        filename: item.filename || item.file_name || `${resourceName}_export`
      }));
    } else {
      toast.info('Not available');
    }
  };

  const submitExportRequest = async (payload, setExportMessage, exportType = 'historical_forecast') => {
    try {
      // Dispatch to the appropriate endpoint based on export type
      let exportAction;
      if (exportType === 'market') {
        exportAction = requestMarketReportExport;
      } else if (exportType === 'raw_measurement') {
        exportAction = requestRawMeasurementsExport;
      } else {
        exportAction = requestIndividualForecastExport;
      }

      const res = await dispatch(exportAction(payload)).unwrap();
      const data = res || {};

      dispatch(fetchExports());
      setExportMessage(data.message || 'Requested');
    } catch (err) {
      const errorData = typeof err === 'string' ? {message: err} : (err || {});
      const errorMessage = errorData?.message || errorData?.error || 'Export request failed';
      const availableResources = errorData?.available_resources;

      if (availableResources && Array.isArray(availableResources)) {
        const resourceNames = availableResources.map(r => r.name || r.resource_name || r).join(', ');
        const detailedMessage = `${errorMessage}\n\nAvailable resources: ${resourceNames}`;
        setExportMessage(errorMessage);
        toast.error(detailedMessage, {autoClose: 8000});
      } else {
        setExportMessage(errorMessage);
        toast.error(errorMessage);
      }
    }
  };

  const refreshExports = async () => {
    setIsRefreshing(true);
    try {
      await dispatch(fetchExports()).unwrap();
      toast.success('Exports list refreshed');
    } catch (err) {
      toast.error('Failed to refresh exports');
    } finally {
      setIsRefreshing(false);
    }
  };

  return {
    fileName,
    setFileName,
    pickedFile,
    setPickedFile,
    editingFile,
    setEditingFile,
    deleteTarget,
    deleteOpen,
    setDeleteOpen,
    resetForm,
    handleFilePick,
    handleUpload,
    handleDownload,
    confirmDelete,
    performDelete,
    handleExportDownload,
    submitExportRequest,
    refreshExports,
    isRefreshing
  };
};

